// src/utils/autoClassify.js

const ZONE_TYPE_LABEL = {
  1: 'Home', 2: 'Office', 3: 'Client', 4: 'Supplier',
  5: 'Site', 6: 'Prospects', 7: 'Private', 8: 'Other',
  9: 'School', 10: 'Medical', 11: 'Shopping',
};

const SA_HOLIDAYS = {
  '2024-01-01': "New Year's Day",        '2024-03-21': 'Human Rights Day',
  '2024-03-29': 'Good Friday',           '2024-04-01': 'Family Day',
  '2024-04-27': 'Freedom Day',           '2024-05-01': "Workers' Day",
  '2024-05-29': 'Election Day',          '2024-06-16': 'Youth Day',
  '2024-06-17': 'Public Holiday',        '2024-08-09': "National Women's Day",
  '2024-09-24': 'Heritage Day',          '2024-12-16': 'Day of Reconciliation',
  '2024-12-25': 'Christmas Day',         '2024-12-26': 'Day of Goodwill',
  '2025-01-01': "New Year's Day",        '2025-03-21': 'Human Rights Day',
  '2025-04-18': 'Good Friday',           '2025-04-21': 'Family Day',
  '2025-04-27': 'Freedom Day',           '2025-04-28': 'Public Holiday',
  '2025-05-01': "Workers' Day",          '2025-06-16': 'Youth Day',
  '2025-08-09': "National Women's Day",  '2025-08-11': 'Public Holiday',
  '2025-09-24': 'Heritage Day',          '2025-12-16': 'Day of Reconciliation',
  '2025-12-25': 'Christmas Day',         '2025-12-26': 'Day of Goodwill',
  '2026-01-01': "New Year's Day",        '2026-03-21': 'Human Rights Day',
  '2026-04-03': 'Good Friday',           '2026-04-06': 'Family Day',
  '2026-04-27': 'Freedom Day',           '2026-05-01': "Workers' Day",
  '2026-06-16': 'Youth Day',             '2026-08-09': "National Women's Day",
  '2026-08-10': 'Public Holiday',        '2026-09-24': 'Heritage Day',
  '2026-12-16': 'Day of Reconciliation', '2026-12-25': 'Christmas Day',
  '2026-12-26': 'Day of Goodwill',
  '2027-01-01': "New Year's Day",        '2027-03-21': 'Human Rights Day',
  '2027-03-22': 'Public Holiday',        '2027-03-26': 'Good Friday',
  '2027-03-29': 'Family Day',            '2027-04-27': 'Freedom Day',
  '2027-05-01': "Workers' Day",          '2027-06-16': 'Youth Day',
  '2027-08-09': "National Women's Day",  '2027-09-24': 'Heritage Day',
  '2027-12-16': 'Day of Reconciliation', '2027-12-25': 'Christmas Day',
  '2027-12-26': 'Day of Goodwill',       '2027-12-27': 'Public Holiday',
};

const BIZ  = 'Business Travel';
const PRIV = 'Private Travel';

const ZONE_PAIR_CATEGORY = {
  'Home→Home':        'private',  'Home→Office':      'private',
  'Home→Client':      'business', 'Home→Supplier':    'business',
  'Home→Site':        'business', 'Home→Prospects':   'business',
  'Home→School':      'private',  'Home→Medical':     'private',
  'Home→Shopping':    'private',  'Home→Private':     'private',
  'Office→Home':      'private',  'Client→Home':      'business',
  'Supplier→Home':    'business', 'Site→Home':        'business',
  'Prospects→Home':   'business', 'Private→Home':     'private',
  'School→Home':      'private',  'Medical→Home':     'private',
  'Shopping→Home':    'private',  'Office→Client':    'business',
  'Office→Supplier':  'business', 'Office→Site':      'business',
  'Office→Prospects': 'business', 'Client→Office':    'business',
  'Supplier→Office':  'business', 'Site→Office':      'business',
  'Prospects→Office': 'business', 'Client→Client':    'business',
  'Client→Supplier':  'business', 'Client→Site':      'business',
  'Client→Prospects': 'business', 'Supplier→Client':  'business',
  'Supplier→Site':    'business', 'Site→Client':      'business',
  'Site→Supplier':    'business', 'Prospects→Client': 'business',
};

const END_ZONE_CATEGORY = {
  'Home': 'private', 'Office': 'private', 'School': 'private',
  'Medical': 'private', 'Shopping': 'private', 'Private': 'private',
  'Client': 'business', 'Supplier': 'business', 'Site': 'business', 'Prospects': 'business',
};

const START_ZONE_CATEGORY = {
  'Home': 'private', 'Office': 'business', 'Client': 'business',
  'Supplier': 'business', 'Site': 'business', 'Prospects': 'business', 'Private': 'private',
};

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function haversineM(lat1, lng1, lat2, lng2) {
  const R    = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a    = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

function matchZoneByCoords(lat, lng, zones) {
  if (!lat || !lng) return null;
  for (const z of zones) {
    const dist = haversineM(lat, lng, Number(z.lat), Number(z.lng));
    if (dist <= z.radius) return z;
  }
  return null;
}

export function resolveReasonId(label, category, dbReasons) {
  const pool  = dbReasons.filter(r => r.category === category);
  const exact = pool.find(r => r.name.toLowerCase() === label.toLowerCase());
  if (exact) return exact.id;
  return pool[0]?.id ?? null;
}

function descriptiveName(name, zoneType) {
  if (!name) return null;
  const typeLabel = ZONE_TYPE_LABEL[zoneType ?? 0];
  return name !== typeLabel ? name : null;
}

export function autoClassifyTrip(trip, schedule, zones = [], periods = []) {
  const d   = new Date(trip.start_time);
  const dow = d.getDay();
  const t   = trip;

  let startZoneType = t.start_zone_type ? Number(t.start_zone_type) : null;
  let endZoneType   = t.end_zone_type   ? Number(t.end_zone_type)   : null;
  let startZoneName = t.start_zone_name ?? null;
  let endZoneName   = t.end_zone_name   ?? null;

  if (!startZoneType && zones.length) {
    const z = matchZoneByCoords(Number(t.start_lat), Number(t.start_lng), zones);
    if (z?.zone_type) { startZoneType = Number(z.zone_type); startZoneName = z.name ?? null; }
  }
  if (!endZoneType && zones.length) {
    const z = matchZoneByCoords(Number(t.end_lat), Number(t.end_lng), zones);
    if (z?.zone_type) { endZoneType = Number(z.zone_type); endZoneName = z.name ?? null; }
  }

  const startZone = ZONE_TYPE_LABEL[startZoneType ?? 0] ?? '';
  const endZone   = ZONE_TYPE_LABEL[endZoneType   ?? 0] ?? '';

  function buildNotes() {
    const endDesc   = descriptiveName(endZoneName,   endZoneType);
    const startDesc = descriptiveName(startZoneName, startZoneType);
    if (endDesc && startDesc) return `${startDesc} to ${endDesc}`;
    if (endDesc)              return endDesc;
    if (startDesc)            return startDesc;
    return undefined;
  }

  function result(cat) {
    return { tripId: trip.id, category: cat, reasonLabel: cat === 'business' ? BIZ : PRIV, reason_detail: buildNotes() };
  }

  const dk = dateKey(d);

  // Tier 0: periods (leave/business_trip override everything)
  for (const p of periods) {
    if (dk >= p.date_from && dk <= p.date_to) {
      return result(p.type === 'leave' ? 'private' : 'business');
    }
  }

  // Tier 1: public holiday → always private
  if (SA_HOLIDAYS[dk]) return result('private');

  // Tier 2: non-working day → always private
  const isWorkingDay = schedule.length > 0
    ? schedule.find(s => s.day_of_week === dow)?.is_working_day ?? false
    : dow >= 1 && dow <= 5;
  if (!isWorkingDay) return result('private');

  // Tier 3: zone pair
  if (startZone && endZone) {
    const cat = ZONE_PAIR_CATEGORY[`${startZone}→${endZone}`];
    if (cat) return result(cat);
  }

  // Tier 4: end zone
  if (endZone) {
    const cat = END_ZONE_CATEGORY[endZone];
    if (cat) return result(cat);
  }

  // Tier 5: start zone
  if (startZone) {
    const cat = START_ZONE_CATEGORY[startZone];
    if (cat) return result(cat);
  }

  // Default: working day, no zone data → business
  return result('business');
}

export function autoClassifyAll(trips, schedule, zones = [], periods = []) {
  return trips.map(t => autoClassifyTrip(t, schedule, zones, periods));
}